/**
 * @description Define CSS Variables to be used for various screen sizes like
 * for mobiles and desktop view (later we can add support for tablets here also).
 *
 * Convention to be used while defining variables -
 * <component_name>-<element_id>
 * where -
 * component_id - States the component name for which the variable is defined for (required)
 * element_id - States the element for which the CSS is defined ex. label, placeholder etc.
 * Eg. - tooltipModal-footerNote
 *
 * Note -
 * By default global based CSS values will be applied, if there's any requirement
 * specific to particular page, we need to use the concept of pageOverrides there.
 * PageOverrides specific changes must be done based on pageId value.
 * PageOverrides will be given higher prefrence order than the global values.
 *
 * Note - Here hyphen (-) is used as a separator element.
 */
const fontFamily = {
  small: 'Avenir-Book',
  medium: 'Avenir-Book',
  bold: 'Avenir-Heavy',
};

const cssVariables = {
  desktop: {
    font: {
      /**
       * The font property is a shorthand property for:
         font-style
         font-variant
         font-weight
         font-size/line-height
         font-family
         Note: The font-size and font-family values are required. 
         If one of the other values is missing, their default value are used.
       */
      global: {
        'tooltipModal-footerNote': `12px ${fontFamily.small}`,
        haveQuestions: `600 18px ${fontFamily.medium}`,
        'leftPanelQuestions-heading': `24px ${fontFamily.medium}`,
        'leftPanelQuestions-item': `16px ${fontFamily.small}`,
        groupQuestionText: `36px ${fontFamily.small}`,
        'singleSelect-defaultRadio': '14px AllStateSans',
        'singleSelect-classic': `18px ${fontFamily.medium}`,
        'singleSelect-card': `600 16px ${fontFamily.medium}`,
        'singleSelect-oval': `600 16px ${fontFamily.medium}`,
        'singleSelect-selectWrapper': `600 14px ${fontFamily.medium}`,
        'singleSelect-rectangle': `18px ${fontFamily.medium}`,
        'singleSelect-label': `18px ${fontFamily.medium}`,
        'multiSelect-label': `18px ${fontFamily.medium}`,
        multiSelect: `18px ${fontFamily.small}`,
        'input-label': `18px ${fontFamily.medium}`,
        'input-placeholder': `18px ${fontFamily.small}`,
        datePicker: `18px ${fontFamily.medium}`,
        'reviewlabel-text': `18px/24px ${fontFamily.small}`,
        'reviewlabel-des': `600 18px/24px ${fontFamily.medium}`,
        'table-heading': `36px ${fontFamily.small}`,
        'table-p': `18px ${fontFamily.small}`,
        'button-next': `18px ${fontFamily.bold}`,
        'button-prev': `18px ${fontFamily.bold}`,
        'button-action': `600 16px ${fontFamily.medium}`,
        'button-list-edit': `600 16px ${fontFamily.medium}`,
        'button-list-delete': `600 16px ${fontFamily.medium}`,
        'button-list-add': `16px ${fontFamily.bold}`,
        'button-list-item-add': `600 16px ${fontFamily.medium}`,
        'button-list-close': `600 16px ${fontFamily.medium}`,
        'button-remove': `600 16px ${fontFamily.medium}`,
        'button-logout': `600 16px ${fontFamily.medium}`,
        'button-modal': `18px ${fontFamily.bold}`,
        'button-update': `600 16px ${fontFamily.medium}`,
        'selectOption-label': `18px ${fontFamily.medium}`,
        'selectOption-placeholder': `18px ${fontFamily.small}`,
        'sectionHeader-quoteHeader': `48px ${fontFamily.bold} !important`,
        'textElement-h2': `30px ${fontFamily.bold} !important`,
        'sso-auth-message-main': `30px ${fontFamily.bold} !important`,
        'sso-auth-message-helper': `18px ${fontFamily.bold} !important`,
      },
      health: {
        'textElement-labelText': `600 14px ${fontFamily.medium}`,
        'input-labelText': `600 14px ${fontFamily.medium}`,
      },
      questions: {
        'textElement-labelText': `18px ${fontFamily.medium}`,
        'input-labelText': `600 14px ${fontFamily.medium}`,
      },
      riders: {
        'input-slider-description': `16px ${fontFamily.small}`,
        'input-slider-text': `600 18px ${fontFamily.medium}`,
        'textElement-labelText': `600 18px ${fontFamily.medium}`,
        'singleSelect-description': `16px ${fontFamily.small}`,
      },
      plan: {
        'textElement-p': `16px ${fontFamily.small}`,
        'table-header-content': `600 24px ${fontFamily.medium}`,
        'sectionHeader-quoteHeader': `30px ${fontFamily.bold} !important`,
      },
      review: {
        'reviewlabel-group': `600 26px ${fontFamily.small}`,
        'button-update': `600 18px ${fontFamily.medium}`,
        'button-list-edit': `600 18px ${fontFamily.medium}`,
        'button-list-delete': `600 18px ${fontFamily.medium}`,
        'textElement-heading': `600 26px ${fontFamily.small}`,
      },
      knockout: {
        'textElement-labelText': `600 26px ${fontFamily.bold}`,
      },
      login: {
        groupQuestionText: `24px ${fontFamily.medium}`,
      },
      inactivesession: {
        'textElement-labelText': `18px/1.7rem ${fontFamily.medium}`,
      },
    },
    margin: {
      global: {
        'reflexiveCard-level1Wrapper': '0',
        'listQuestions-listWrapper': '30px 0 10px',
        'listQuestionCard-wrapper': '0',
      },
      quote: {
        'sectionHeader-quoteHeader': '0 auto',
      },
      plan: {
        'reflexiveCard-level1Wrapper': '0% 0% 0% 0%',
        'sectionHeader-quoteHeader': '20px auto',
      },
      questions: {
        'right-rightWrapper': '40px 40px 120px',
        'sectionHeader-quoteHeader': '0 auto',
      },
      signature: {
        'right-rightWrapper': '40px 40px 120px !important',
        'sectionHeader-quoteHeader': '20px auto',
      },
      challenge: {
        'right-rightWrapper': '20px 40px 40px',
      },
      inactivesession: {
        'sectionHeader-quoteHeader': '0 auto',
      },
    },
    padding: {
      /**
        * The padding property is a shorthand property for:
          padding-top
          padding-right
          padding-bottom
          padding-left
          Note: Padding creates extra space within an element, 
          while margin creates extra space around an element.
        */
      global: {
        card: '20px 0 0',
        'listQuestionCard-wrapper': '20px',
        'reflexiveCard-level1Wrapper': '20px',
        'leftPanelQuestions-tableDiv': 'clamp(5px, 5%, 20px)',
        'haveQuestions-haveQuestionsWrapper': '0px 0px 0px 0px',
        'reviewBreadcrumb-wrapper':
          'clamp(24px, 5%, 60px) clamp(24px, 5%, 100px) clamp(24px, 5%, 40px) clamp(54px, 5%, 100px);',
      },
      quote: {
        card: '10px 0 0',
      },
      riders: {
        'haveQuestions-haveQuestionsWrapper': '50px 0px 0px 0px',
      },
      plan: {
        card: '0px 0px 20px 0px',
      },
      questions: {
        card: '10px 0 0',
      },
      quotereview: {
        card: 'clamp(24px, 5%, 60px) clamp(16px, 5%, 100px) clamp(24px, 5%, 40px) clamp(16px, 5%, 100px)',
      },
      review: {
        card: 'clamp(24px, 5%, 60px) clamp(16px, 5%, 100px) clamp(24px, 5%, 40px) clamp(16px, 5%, 100px)',
      },
      knockout: {
        card: '0px',
      },
    },
    top: {
      /**
       * The top property affects the vertical position of a positioned element.
       * This property has no effect on non-positioned elements.
       */
      global: {},
    },
    height: {
      /**
       * The height property sets the height of an element.
       * Note: The min-height and max-height properties override the height property.
       */
      global: {
        'content-main': '465px',
      },
    },
    width: {
      /**
        * The width property sets the width of an element.
          The width of an element does not include padding, borders, or margins!
          Note: The min-width and max-width properties override the width property.
        */
      global: {
        'left-normalScreenWrapper': '33%',
        'right-rightWrapper': '67%',
      },
      quote: {
        card: '90%',
        'actionButton-fullWidthButton': '90%',
        'sectionHeader-quoteHeader': '90%',
      },
      plan: {
        'right-rightWrapper': '85%',
      },
      riders: {
        'footerNotes-footerWrapper': '90%',
      },
      questions: {
        'left-normalScreenWrapper': '40%',
        'right-rightWrapper': '60%',
        card: '100%',
        'footerNotes-footerWrapper': '90%',
      },
      payment: {
        'footerNotes-footerWrapper': '90%',
      },
      knockout: {
        card: '100%',
      },
      review: {
        'sectionHeader-quoteHeader': '90%',
        'right-rightWrapper': '80%',
      },
      signature: {
        'right-rightWrapper': '80%',
      },
      timeout: {
        card: '100%',
      },
    },
    maxWidth: {
      /**
        * The max-width property defines the maximum width of an element.
          If the content is larger than the maximum width, it will automatically change the height of the element.
          If the content is smaller than the maximum width, the max-width property has no effect.
          Note: This prevents the value of the width property from becoming larger than max-width. 
          The value of the max-width property overrides the width property.
        */
      global: {
        'footerNotes-footerWrapper': '80%',
        'haveQuestions-haveQuestionsWrapper': '80%',
        'right-rightWrapper': '980px',
        'left-normalScreenWrapper': '330px',
      },
      quote: {
        'sectionHeader-quoteHeader': '780px',
      },
      plan: {
        card: '870px !important',
        'sectionHeader-quoteHeader': '870px',
      },
      riders: {
        'footerNotes-footerWrapper': '780px',
      },
      questions: {
        'footerNotes-footerWrapper': '780px',
        'left-normalScreenWrapper': '500px',
      },
      payment: {
        'footerNotes-footerWrapper': '780px',
      },
      knockout: {
        card: '90%',
        'right-rightWrapper': '1200px',
        'sectionHeader-quoteHeader': '100%',
      },
      challenge: {
        'right-rightWrapper': '600px',
      },
      review: {
        'sectionHeader-quoteHeader': '80%',
        'right-rightWrapper': '100%',
      },
      timeout: {
        card: '780px !important',
      },
    },
  },
  tablet: {
    font: {
      /**
        * The font property is a shorthand property for:
          font-style
          font-variant
          font-weight
          font-size/line-height
          font-family
          Note: The font-size and font-family values are required. 
          If one of the other values is missing, their default value are used.
        */
      global: {
        'tooltipModal-footerNote': `12px ${fontFamily.small}`,
        haveQuestions: `600 16px/24px ${fontFamily.medium}`,
        'leftPanelQuestions-heading': `18px ${fontFamily.medium}`,
        'leftPanelQuestions-item': `16px ${fontFamily.small}`,
        groupQuestionText: `24px ${fontFamily.small}`,
        'singleSelect-defaultRadio': '14px AllStateSans',
        'singleSelect-classic': `18px ${fontFamily.medium}`,
        'singleSelect-card': `600 16px ${fontFamily.medium}`,
        'singleSelect-oval': `600 14px ${fontFamily.medium}`,
        'singleSelect-selectWrapper': `600 14px ${fontFamily.medium}`,
        'singleSelect-label': `18px ${fontFamily.medium}`,
        'multiSelect-label': `18px ${fontFamily.medium}`,
        multiSelect: `18px ${fontFamily.small}`,
        'input-label': `18px ${fontFamily.medium}`,
        'input-placeholder': `18px ${fontFamily.small}`,
        datePicker: `18px ${fontFamily.medium}`,
        'reviewlabel-text': `18px/24px ${fontFamily.small}`,
        'reviewlabel-des': `600 18px/24px ${fontFamily.medium}`,
        'table-heading': `36px ${fontFamily.small}`,
        'table-p': `18px ${fontFamily.small}`,
        'button-next': `18px ${fontFamily.bold}`,
        'button-prev': `18px ${fontFamily.bold}`,
        'button-action': `600 16px/20px ${fontFamily.medium}`,
        'button-list-edit': `600 16px/20px ${fontFamily.medium}`,
        'button-list-delete': `600 16px/20px ${fontFamily.medium}`,
        'button-list-add': `16px ${fontFamily.bold}`,
        'button-list-item-add': `600 16px/20px ${fontFamily.medium}`,
        'button-list-close': `600 16px/20px ${fontFamily.medium}`,
        'button-remove': `600 16px/20px ${fontFamily.medium}`,
        'button-logout': `600 16px/20px ${fontFamily.medium}`,
        'button-modal': `18px ${fontFamily.bold}`,
        'button-update': `600 16px/20px ${fontFamily.medium}`,
        'selectOption-label': `18px ${fontFamily.medium}`,
        'selectOption-placeholder': `18px ${fontFamily.small}`,
        'sectionHeader-quoteHeader': `48px ${fontFamily.bold} !important`,
        'textElement-h2': `30px ${fontFamily.bold} !important`,
        'sso-auth-message-main': `30px ${fontFamily.bold} !important`,
        'sso-auth-message-helper': `18px ${fontFamily.bold} !important`,
      },
      health: {
        'textElement-labelText': `600 14px ${fontFamily.medium}`,
        'input-labelText': `600 14px ${fontFamily.medium}`,
      },
      questions: {
        'textElement-labelText': `18px ${fontFamily.medium}`,
        'input-labelText': `600 14px ${fontFamily.medium}`,
      },
      riders: {
        'input-slider-description': `16px ${fontFamily.small}`,
        'input-slider-text': `600 18px ${fontFamily.medium}`,
        'textElement-labelText': `600 18px ${fontFamily.medium}`,
        'singleSelect-description': `16px ${fontFamily.small}`,
      },
      plan: {
        'textElement-p': `16px ${fontFamily.small}`,
        'table-header-content': `600 24px ${fontFamily.medium}`,
        'sectionHeader-quoteHeader': `30px ${fontFamily.bold} !important`,
      },
      review: {
        'reviewlabel-group': `600 30px ${fontFamily.small}`,
        'button-update': `600 18px ${fontFamily.medium}`,
        'button-list-edit': `600 18px ${fontFamily.medium}`,
        'button-list-delete': `600 18px ${fontFamily.medium}`,
        'textElement-heading': `600 30px ${fontFamily.small}`,
      },
      knockout: {
        'textElement-labelText': `600 26px ${fontFamily.bold}`,
      },
      login: {
        groupQuestionText: `24px ${fontFamily.medium}`,
      },
    },
    margin: {
      global: {
        'reflexiveCard-level1Wrapper': '0',
        'listQuestions-listWrapper': '30px 0 10px',
        'listQuestionCard-wrapper': '0',
      },
      quote: {
        'sectionHeader-quoteHeader': '0 auto',
      },
      plan: {
        'reflexiveCard-level1Wrapper': '0% 0% 0% 0%',
        'sectionHeader-quoteHeader': '0 auto',
      },
      questions: {
        'right-rightWrapper': '40px 40px 120px',
        'sectionHeader-quoteHeader': '0 auto',
      },
      signature: {
        'right-rightWrapper': '40px 40px 120px !important',
        'sectionHeader-quoteHeader': '20px auto',
      },
      knockout: {
        'reviseQuoteButton-reviseButton': '-33px',
      },
    },
    padding: {
      /**
             * The padding property is a shorthand property for:
                padding-top
                padding-right
                padding-bottom
                padding-left
                Note: Padding creates extra space within an element, 
                while margin creates extra space around an element.
             */
      global: {
        card: '20px 0 0',
        'listQuestionCard-wrapper': '20px',
        'reflexiveCard-level1Wrapper': '20px',
        'leftPanelQuestions-tableDiv': 'clamp(5px, 5%, 20px)',
        'haveQuestions-haveQuestionsWrapper': '0px 0px 0px 0px',
        'reviewBreadcrumb-wrapper':
          'clamp(24px, 5%, 60px) clamp(24px, 5%, 100px) clamp(24px, 5%, 40px) clamp(54px, 5%, 100px);',
      },
      quote: {
        card: '20px 0 0',
      },
      riders: {
        'haveQuestions-haveQuestionsWrapper': '50px 0px 0px 0px',
      },
      plan: {
        card: '0px 0px 10px 0px',
      },
      questions: {
        card: '10px 0 0',
      },
      quotereview: {
        card: 'clamp(24px, 5%, 60px) clamp(16px, 5%, 100px) clamp(24px, 5%, 40px) clamp(16px, 5%, 100px)',
      },
      review: {
        card: 'clamp(24px, 5%, 60px) clamp(16px, 5%, 100px) clamp(24px, 5%, 40px) clamp(16px, 5%, 100px)',
      },
    },
    top: {
      /**
       * The top property affects the vertical position of a positioned element.
       * This property has no effect on non-positioned elements.
       */
      global: {},
    },
    height: {
      /**
       * The height property sets the height of an element.
       * Note: The min-height and max-height properties override the height property.
       */
      global: {
        'content-main': '465px',
      },
    },
    width: {
      /**
        * The width property sets the width of an element.
          The width of an element does not include padding, borders, or margins!
          Note: The min-width and max-width properties override the width property.
        */
      global: {
        'left-normalScreenWrapper': '33%',
        'right-rightWrapper': '67%',
      },
      quote: {
        card: '90%',
        'actionButton-fullWidthButton': '90%',
        'sectionHeader-quoteHeader': '90%',
      },
      plan: {
        card: '100%',
        'right-rightWrapper': '90%',
      },
      riders: {
        'footerNotes-footerWrapper': '90%',
      },
      questions: {
        'left-normalScreenWrapper': '40%',
        'right-rightWrapper': '60%',
        card: '100%',
        'footerNotes-footerWrapper': '90%',
      },
      payment: {
        'footerNotes-footerWrapper': '90%',
      },
      review: {
        'sectionHeader-quoteHeader': '90%',
        'right-rightWrapper': '100%',
      },
      signature: {
        'right-rightWrapper': '80%',
      },
      timeout: {
        card: '100%',
      },
    },
    maxWidth: {
      /**
        * The max-width property defines the maximum width of an element.
          If the content is larger than the maximum width, it will automatically change the height of the element.
          If the content is smaller than the maximum width, the max-width property has no effect.
          Note: This prevents the value of the width property from becoming larger than max-width. 
          The value of the max-width property overrides the width property.
            */
      global: {
        'footerNotes-footerWrapper': '80%',
        'haveQuestions-haveQuestionsWrapper': '80%',
        'right-rightWrapper': '90%',
      },
      riders: {
        'footerNotes-footerWrapper': '780px',
      },
      questions: {
        'footerNotes-footerWrapper': '780px',
        'left-normalScreenWrapper': '400px',
      },
      payment: {
        'footerNotes-footerWrapper': '780px',
      },
      knockout: {
        'sectionHeader-quoteHeader': '100%',
      },
      challenge: {
        'right-rightWrapper': '350px',
      },
      review: {
        'sectionHeader-quoteHeader': '80%',
        'right-rightWrapper': '100%',
      },
      timeout: {
        card: '780px !important',
      },
    },
  },
  mobile: {
    font: {
      /**
        * The font property is a shorthand property for:
          font-style
          font-variant
          font-weight
          font-size/line-height
          font-family
          Note: The font-size and font-family values are required. 
          If one of the other values is missing, their default value are used.
        */
      global: {
        'tooltipModal-footerNote': '16px',
        haveQuestions: `14px ${fontFamily.medium}`,
        'leftPanelQuestions-heading': `20px ${fontFamily.medium}`,
        'leftPanelQuestions-item': `16px ${fontFamily.small}`,
        groupQuestionText: `24px ${fontFamily.medium}`,
        'singleSelect-defaultRadio': `14px ${fontFamily.small}`,
        'singleSelect-classic': `18px ${fontFamily.medium}`,
        'singleSelect-card': `600 16px ${fontFamily.medium}`,
        'singleSelect-oval': `600 12px ${fontFamily.medium}`,
        'singleSelect-selectWrapper': `600 14px ${fontFamily.medium}`,
        'singleSelect-label': `18px ${fontFamily.medium}`,
        'multiSelect-label': `18px ${fontFamily.medium}`,
        multiSelect: `18px ${fontFamily.small}`,
        'input-label': `18px ${fontFamily.medium}`,
        'input-placeholder': `18px ${fontFamily.small}`,
        datePicker: `18px ${fontFamily.medium}`,
        'reviewlabel-text': `18px/24px ${fontFamily.small}`,
        'reviewlabel-des': `600 18px/24px ${fontFamily.medium}`,
        'table-heading': `26px ${fontFamily.small}`,
        'table-p': `11px ${fontFamily.small}`,
        'button-next': `18px ${fontFamily.bold}`,
        'button-prev': `18px ${fontFamily.bold}`,
        'button-action': `600 16px ${fontFamily.medium}`,
        'button-list-edit': `600 16px ${fontFamily.medium}`,
        'button-list-delete': `600 16px ${fontFamily.medium}`,
        'button-list-add': `16px ${fontFamily.bold}`,
        'button-list-item-add': `600 16px ${fontFamily.medium}`,
        'button-list-close': `600 16px ${fontFamily.medium}`,
        'button-remove': `600 16px ${fontFamily.medium}`,
        'button-logout': `600 16px ${fontFamily.medium}`,
        'button-modal': `18px ${fontFamily.bold}`,
        'button-update': `600 16px ${fontFamily.medium}`,
        'selectOption-label': `18px ${fontFamily.medium}`,
        'selectOption-placeholder': `18px ${fontFamily.small}`,
        'sectionHeader-quoteHeader': `48px ${fontFamily.bold} !important`,
        'textElement-h2': `30px ${fontFamily.bold} !important`,
        'sso-auth-message-main': `30px ${fontFamily.bold} !important`,
        'sso-auth-message-helper': `18px ${fontFamily.bold} !important`,
      },
      health: {
        'textElement-labelText': `600 14px ${fontFamily.medium}`,
        'input-labelText': `600 14px ${fontFamily.medium}`,
      },
      questions: {
        'textElement-labelText': `18px ${fontFamily.medium}`,
        'input-labelText': `600 14px ${fontFamily.medium}`,
      },
      riders: {
        'input-slider-description': `16px ${fontFamily.small}`,
        'input-slider-text': `18px ${fontFamily.medium}`,
        'textElement-labelText': `18px ${fontFamily.medium}`,
        'singleSelect-description': `16px ${fontFamily.small}`,
      },
      plan: {
        'textElement-p': `16px ${fontFamily.small}`,
        'table-header-content': `600 4vw ${fontFamily.medium}`,
        'sectionHeader-quoteHeader': `30px ${fontFamily.bold} !important`,
      },
      review: {
        'reviewlabel-group': `600 30px ${fontFamily.medium}`,
        'button-update': `600 18px ${fontFamily.medium}`,
        'button-list-edit': `600 18px ${fontFamily.medium}`,
        'button-list-delete': `600 18px ${fontFamily.medium}`,
        'textElement-heading': `600 30px ${fontFamily.small}`,
      },
      knockout: {
        'textElement-labelText': `600 20px ${fontFamily.bold}`,
      },
      login: {
        groupQuestionText: `24px ${fontFamily.medium}`,
      },
    },
    margin: {
      global: {
        'reflexiveCard-level1Wrapper': '0',
        'listQuestions-listWrapper': '30px 0 10px',
        'listQuestionCard-wrapper': '0',
      },
      quote: {
        'sectionHeader-quoteHeader': '0 auto',
      },
      plan: {
        'reflexiveCard-level1Wrapper': '0% 0% 0% 0%',
        'sectionHeader-quoteHeader': '0 auto',
      },
      questions: {
        'right-rightWrapper': '20px auto',
        'sectionHeader-quoteHeader': '0 auto',
      },
      signature: {
        'right-rightWrapper': '40px auto 120px !important',
      },
      challenge: {
        'right-rightWrapper': '20px 40px 40px',
        'sectionHeader-quoteHeader': '20px 0',
      },
      knockout: {
        'reviseQuoteButton-reviseButton': '-70px',
      },
    },
    padding: {
      /**
             * The padding property is a shorthand property for:
                padding-top
                padding-right
                padding-bottom
                padding-left
                Note: Padding creates extra space within an element, 
                while margin creates extra space around an element.
             */
      global: {
        card: '20px 0 0',
        'listQuestionCard-wrapper': '8px',
        'reflexiveCard-level1Wrapper': '8px',
        'haveQuestions-haveQuestionsWrapper': '0px 0px',
        'leftPanelQuestions-tableDiv': 'clamp(5px, 5%, 20%)',
        'reviewBreadcrumb-wrapper':
          'clamp(24px, 5%, 60px) clamp(24px, 5%, 100px) clamp(24px, 5%, 40px) clamp(24px, 5%, 100px);',
      },
      quote: {
        card: '20px 0 0',
      },
      plan: {
        card: '0px 0px 10px 0px',
      },
      questions: {
        card: '20px 0 0',
      },
      quotereview: {
        card: 'clamp(24px, 5%, 60px) clamp(16px, 5%, 100px) clamp(24px, 5%, 40px) clamp(16px, 5%, 100px)',
      },
      review: {
        card: 'clamp(24px, 5%, 60px) clamp(16px, 5%, 100px) clamp(24px, 5%, 40px) clamp(16px, 5%, 100px)',
      },
    },
    top: {
      /**
       * The top property affects the vertical position of a positioned element.
       * This property has no effect on non-positioned elements.
       */
      global: {},
      riders: {
        'leftPanelQuestions-tableWrapper': '45px',
      },
      payment: {
        'leftPanelQuestions-tableWrapper': '200px',
      },
    },
    height: {
      /**
       * The height property sets the height of an element.
       * Note: The min-height and max-height properties override the height property.
       */
      global: {
        'content-main': '465px',
      },
      questions: {
        'content-main': '918px',
      },
    },
    width: {
      /**
             * The width property sets the width of an element.
                The width of an element does not include padding, borders, or margins!
                Note: The min-width and max-width properties override the width property.
             */
      global: {},
      quote: {
        card: '97% !important',
        'actionButton-fullWidthButton': '100%',
        'sectionHeader-quoteHeader': '100%',
      },
      riders: {
        'footerNotes-footerWrapper': '100%',
      },
      questions: {
        'footerNotes-footerWrapper': '100%',
        card: '100%',
      },
      payment: {
        'footerNotes-footerWrapper': '100%',
      },
      review: {
        'sectionHeader-quoteHeader': '95%',
      },
      signature: {
        'right-rightWrapper': '90%',
      },
    },
    maxWidth: {
      /**
             * The max-width property defines the maximum width of an element.
                If the content is larger than the maximum width, it will automatically change the height of the element.
                If the content is smaller than the maximum width, the max-width property has no effect.
                Note: This prevents the value of the width property from becoming larger than max-width. 
                The value of the max-width property overrides the width property.
             */
      global: {
        'footerNotes-footerWrapper': '100%',
        'haveQuestions-haveQuestionsWrapper': '100%',
        'right-rightWrapper': '100%',
      },
      knockout: {
        'sectionHeader-quoteHeader': '100%',
      },
      challenge: {
        'sectionHeader-quoteHeader': '100%',
      },
    },
  },
};

export default cssVariables;
